/**
 * ?BigCalendar Component
 */

import { DateTime } from 'luxon';
import { FC, useEffect, useState } from 'react';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi2';

import * as allStyles from './BigCalendarStyles';

export type BigCalendar003Props = {
  /**
   * Description of themes in Storybook
   */
  theme?: 'theme_default';
  /**
   * Class Name override
   */
  customTheme?: any;
  /**
   * Custom Calsses
   */
  classNames?: string;
  /**
   * Optional click handler
   */
  onClick?: () => void;

  selectedDate?: any | null;
  onDateSelected?: (date: any) => void;

  isDateEnabled?: (date: any) => boolean;

  onMonthSelected?: (monthName?: string, monthNumber?: number, year?: number) => void;

  showClearButton?: boolean;
  onDoneClick?: () => void;
};

/**
 * Description of BigCalendar component displayed in Storybook
 */

const BigCalendar003: FC<BigCalendar003Props> = ({
  theme = 'theme_default',
  customTheme,
  classNames = '',
  selectedDate = DateTime.now(),
  onDateSelected = (date: DateTime) => {},
  isDateEnabled = (date: DateTime) => {
    return true;
  },
  onMonthSelected = (monthName: string, monthNumber?: number, year?: number) => {
    return true;
  },
  onDoneClick = () => {},
  showClearButton = false,
  ...props
}) => {
  const [today, setToday] = useState(DateTime.now());
  const [activeDate, setActiveDate] = useState(selectedDate);
  const [startDate, setStartDate] = useState(
    selectedDate ? selectedDate.startOf('month') : DateTime.now().startOf('month'),
  );
  const [startDateWeekDay, setStartDateWeekDay] = useState(
    selectedDate ? selectedDate.startOf('month').weekday : DateTime.now().startOf('month').weekday,
  );
  const [month, setMonth] = useState(startDate.monthLong);
  const [year, setYear] = useState(startDate.year);

  let selectedTheme: any = allStyles.themes[theme];
  selectedTheme = customTheme ?? selectedTheme;

  const setPreviousMonth = () => {
    if (startDate.month === 1) {
      const newDate = startDate.set({
        month: 12,
        year: startDate.year - 1,
      });
      setStartDate(newDate);
      setMonth(newDate?.monthLong);
      setYear(newDate?.year);
      setStartDateWeekDay(newDate.weekday);
    } else {
      const newDate = startDate.set({
        month: Math.max(1, startDate.month - 1),
      });
      setStartDate(newDate);
      setMonth(newDate?.monthLong);
      setStartDateWeekDay(newDate.weekday);
    }
  };

  const setNextMonth = () => {
    if (startDate.month === 12) {
      const newDate = startDate.set({
        month: 1,
        year: startDate.year + 1,
      });
      setStartDate(newDate);
      setMonth(newDate?.monthLong);
      setYear(newDate?.year);
      setStartDateWeekDay(newDate.weekday);
    } else {
      const newDate = startDate.set({
        month: Math.min(12, startDate.month + 1),
      });
      setStartDate(newDate);
      setMonth(newDate?.monthLong);
      setStartDateWeekDay(newDate.weekday);
    }
  };

  const getDates = (startDate: DateTime, weekday: number) => {
    const dates: Array<DateTime> = [];
    let date = DateTime.fromJSDate(startDate.toJSDate());
    let monthEndDate = DateTime.fromJSDate(startDate.toJSDate());

    date = date.minus({
      days: weekday - 1,
    });

    monthEndDate = monthEndDate.endOf('month');
    // .plus({
    //   days: 6 - weekday,
    // });

    // console.log(startDate.toJSDate(), weekday, date.toString());
    // console.log(monthEndDate.toString());
    // console.log(monthEndDate.diff(date, "days").days);

    dates.push(DateTime.fromJSDate(date.toJSDate()));
    date = date.plus({
      days: 1,
    });
    // console.log(monthEndDate.diff(date, "days").days);

    while (monthEndDate.diff(date, 'days').days >= 0) {
      dates.push(DateTime.fromJSDate(date.toJSDate()));
      date = date.plus({
        days: 1,
      });
    }
    return dates;
  };

  // useEffect(() => {
  //   if (selectedDate instanceof DateTime && !activeDate.equals(selectedDate)) {
  //     setActiveDate(DateTime.fromJSDate(selectedDate.toJSDate()));
  //   }
  // }, [selectedDate]);

  useEffect(() => {
    onMonthSelected(month === null ? '' : month, startDate.month, startDate.year);
  }, [month]);

  useEffect(() => {
    setStartDate(selectedDate ? selectedDate.startOf('month') : DateTime.now().startOf('month'));
    setStartDateWeekDay(
      selectedDate ? selectedDate.startOf('month').weekday : DateTime.now().startOf('month').weekday,
    );
    setMonth(selectedDate?.monthLong);
    setYear(selectedDate?.year);

    if (selectedDate) {
      setActiveDate(DateTime.fromISO(selectedDate.toISO()).startOf('day'));
    }
  }, [selectedDate?.day]);

  return (
    <div
      className={[
        'big-calendar-001',
        selectedTheme.main,
        selectedTheme.baseStyles ?? allStyles.baseStyles,
        classNames,
      ].join(' ')}
      {...props}
    >
      <div className="flex flex-col w-full px-1 pt-5 md:pb-6">
        <ul className="hidden gap-[15px] mb-[15px] justify-center ">
          <li className=" bg-[#f3f5f6] rounded-[15px] px-[15px] py-[10px]">
            <button
              className="w-full h-full bg-transparent border-transparent"
              onClick={() => {
                setActiveDate(DateTime.now());
                onDateSelected(DateTime.now());
              }}
            >
              Today
            </button>
          </li>
          <li className=" bg-[#f3f5f6] rounded-[15px] px-[15px] py-[10px]">
            <button
              className="w-full h-full bg-transparent border-transparent "
              onClick={() => {
                setActiveDate(DateTime.now().plus({ days: 1 }));
                onDateSelected(DateTime.now().plus({ days: 1 }));
              }}
            >
              Tomorrow
            </button>
          </li>
          <li className=" bg-[#f3f5f6] rounded-[15px] px-[15px] py-[10px]">
            <button
              className="w-full h-full bg-transparent border-transparent "
              onClick={() => {
                setActiveDate(DateTime.now().plus({ days: 2 }));
                onDateSelected(DateTime.now().plus({ days: 2 }));
              }}
            >
              In 2 days
            </button>
          </li>
        </ul>
        <div className="flex items-center justify-center space-x-12">
          <button className="flex items-center justify-center" onClick={() => setPreviousMonth()}>
            <HiChevronLeft className="h-5 w-5 text-gray-500" />
          </button>
          <div className="text-base font-medium flex flex-row space-x-2 items-center mx-auto">
            <div className="text-[12px] md:text-[17px] text-[#323231]">{month}</div>
            <div className="text-[12px] md:text-[17px]">{year}</div>
          </div>
          <button className="flex items-center justify-center" onClick={() => setNextMonth()}>
            <HiChevronRight className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-2 text-[18px] leading-[23px] text-center  text-[#323231] my-6 auto-cols-[minmax(0,_2fr)]">
          <span className="flex items-center justify-center h-[40px] w-[40px] font-extrabold  text-[13px] md:text-[16px]">
            M
          </span>
          <span className="flex items-center justify-center h-[40px] w-[40px] font-extrabold  text-[13px] md:text-[16px]">
            T
          </span>
          <span className="flex items-center justify-center h-[40px] w-[40px] font-extrabold  text-[13px] md:text-[16px]">
            W
          </span>
          <span className="flex items-center justify-center h-[40px] w-[40px] font-extrabold  text-[13px] md:text-[16px]">
            T
          </span>
          <span className="flex items-center justify-center h-[40px] w-[40px] font-extrabold  text-[13px] md:text-[16px]">
            F
          </span>
          <span className="flex items-center justify-center h-[40px] w-[40px] font-extrabold  text-[13px] md:text-[16px]">
            S
          </span>
          <span className="flex items-center justify-center h-[40px] w-[40px]  font-extrabold  text-[13px] md:text-[16px]">
            S
          </span>

          {getDates(startDate, startDateWeekDay).map((date) => {
            return (
              <span
                key={date.toUnixInteger()}
                className={[
                  'flex items-center justify-center h-[40px] w-[40px] text-base md:text-[16px]',
                  activeDate && date.equals(activeDate)
                    ? 'text-white bg-black rounded-full '
                    : date.monthLong === month
                    ? isDateEnabled(date)
                      ? 'bg-[#DBD6C9] text-[#FFFFFF] cursor-pointer rounded-full font-bold'
                      : 'text-gray-400 pointer-events-none'
                    : 'bg-transparent text-[#9E9E9D] pointer-events-none',
                ].join(' ')}
              >
                <button
                  className="w-full h-full bg-transparent border-transparent "
                  onClick={() => {
                    setActiveDate(date);
                    onDateSelected(date);
                  }}
                >
                  {date.day}
                </button>
              </span>
            );
          })}
        </div>
        {showClearButton && (
          <div className="flex gap-[16px] mt-[15px]">
            <button
              className=" bg-[#f3f5f6] p-[15px] rounded-[15px] w-1/2 text-[20px]"
              onClick={() => {
                selectedDate = null;
                setActiveDate(null);
                onDateSelected(selectedDate);
              }}
            >
              Clear
            </button>
            <button
              className=" bg-[#E5FD6E] p-[15px] text-[#000] rounded-[15px] w-1/2 text-[20px]"
              onClick={onDoneClick}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BigCalendar003;
